import { Box, CircularProgress, Typography } from '@mui/material';
import { FC, useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../aws/AuthProvider';
import { DataContext } from '../../../aws/DataProvider';
import {
  generateRezept,
  getOrderFollowupRequest,
  getOrderRequest,
  handleComment,
  handleCommentFollowup,
  handleFollowup,
  handleRequest,
  listOrderRequests,
} from '../../../aws/RequestsApi';
import { useStyles } from '../../../theme/styles.helpers';
import { Utils, Orders } from '@hellohair/types';
import { RequestModalStyles } from './RequestModal.Styles';
import { RequestModalProduct } from './RequestModalProduct';
import { RequestModalDosierung } from './RequestModalDosierung';
import { Survey } from '../../survey/Survey';
import { RequestModalActions } from './RequestModalActions';
import { PatientCard } from '../../patient-card/PatientCard';
import { ListCard } from '../../list-card/ListCard';

const formatDate = (timestamp: number): string => new Date(timestamp).toLocaleDateString('de-DE');

interface RequestModalProps {
  onModalClose: () => void;
  requestId: string | undefined;
  isFollowup: boolean;
}

const EXCLUDED_QANDA_TYPES = ['UploadImages', 'Finish', 'Welcome', 'Interstitial'];

export const RequestModal: FC<RequestModalProps> = ({ onModalClose, requestId, isFollowup }) => {
  const { apiClient } = useContext(AuthContext);
  const styles = useStyles(RequestModalStyles, {});
  const {
    ordersState: [requests, setRequests],
  } = useContext(DataContext);

  const [orderDetails, setOrderDetails] =
    useState<Utils.DBItem<Orders.OrderRequestDetails | Orders.OrderFollowupRequestDetails>>();
  const [orderHistory, setOrderHistory] = useState<Utils.DBItem<Orders.OrderRequest>[]>([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (requestId) {
      setLoading(true);
      fetchData(requestId);
    }
  }, [requestId]);

  const fetchData = async (reqId: string) => {
    try {
      const details = await (isFollowup
        ? getOrderFollowupRequest(reqId, apiClient, 'network-only')
        : getOrderRequest(reqId, apiClient, 'network-only'));
      setOrderDetails(details);
      setComment(details.comment ?? '');

      const history = await listOrderRequests({
        apiClient,
        fetchPolicy: 'network-only',
        email: details.customer.email,
      });
      setOrderHistory(history ?? []);
    } catch (error) {
      console.error('[RequestModal] fetchData error:', error);
    } finally {
      setLoading(false);
    }
  };

  const onDeny = async () => {
    if (!orderDetails) return;
    try {
      setLoading(true);
      if (isFollowup) await handleFollowup(orderDetails.requestId, 'DENY', apiClient);
      else await handleRequest(orderDetails.requestId, 'DENY', apiClient);

      setRequests(
        requests.map((req) =>
          req.requestId === orderDetails.requestId ? { ...req, orderState: Orders.OrderRequestState.DENIED } : req
        )
      );
      await fetchData(orderDetails.requestId);
    } catch (error) {
      console.error('[RequestModal] onDeny error:', error);
    } finally {
      setLoading(false);
    }
  };

  const onAccept = async () => {
    if (!orderDetails || !('product' in orderDetails)) return;
    try {
      setLoading(true);
      const response = await generateRezept(
        orderDetails.product.productId,
        orderDetails.requestId,
        orderDetails.customer.customerId,
        false,
        isFollowup,
        apiClient
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const doc = response?.members?.find((m: any) => m.isSigner);
      if (doc?.id) {
        window.open(`https://sign.sproof.com/#/editor/${doc.id}`, '_blank')?.focus();
      }
      setRequests(
        requests.map((req) =>
          req.requestId === orderDetails.requestId ? { ...req, orderState: Orders.OrderRequestState.ALLOWED } : req
        )
      );
      await fetchData(orderDetails.requestId);
      onModalClose();
    } catch (error) {
      console.error('[RequestModal] onAccept error:', error);
    } finally {
      setLoading(false);
    }
  };

  const onSaveComment = () => {
    if (!orderDetails) return;
    if (isFollowup) handleCommentFollowup(orderDetails.requestId, comment, apiClient);
    else handleComment(orderDetails.requestId, comment, apiClient);
  };

  const filteredQanda = orderDetails?.qanda?.filter((q) => !EXCLUDED_QANDA_TYPES.includes(q.type)) ?? [];

  const previousOrders = orderHistory.filter(
    (req) => req.orderState === Orders.OrderRequestState.ALLOWED && req.requestId !== requestId
  );

  const orderHistoryWithoutCurrent = orderHistory.filter((req) => req.requestId !== requestId);

  return (
    <Box sx={styles.root} style={{ pointerEvents: loading ? 'none' : 'all' }}>
      {loading ? (
        <CircularProgress size={32} sx={styles.spinner} thickness={5} />
      ) : (
        <>
          {/* Header */}
          <Box sx={styles.header}>
            <Box sx={styles.typeBadge}>💊 Rezeptanfrage</Box>
            {orderDetails && <Typography sx={styles.requestId}>#{orderDetails.requestId}</Typography>}
            <Box sx={styles.closeButton} onClick={onModalClose}>
              ×
            </Box>
          </Box>

          {/* Body */}
          <Box sx={styles.body}>
            {/* Left Column */}
            <Box sx={styles.leftColumn}>
              {orderDetails && 'product' in orderDetails && (
                <>
                  <RequestModalProduct
                    productName={orderDetails.product.productName}
                    isSubscription={orderDetails.isSubscription}
                    indication={Orders.formatSurveyAnswer(filteredQanda[0]?.answer)}
                  />
                  <RequestModalDosierung product={orderDetails.product} />
                </>
              )}

              <Survey qanda={orderDetails?.qanda ?? []} assets={orderDetails?.assets ?? []} />

              <RequestModalActions
                comment={comment}
                onCommentChange={setComment}
                onCommentBlur={onSaveComment}
                onDeny={onDeny}
                onAccept={onAccept}
                orderState={orderDetails?.orderState}
              />
            </Box>

            {/* Right Column */}
            <Box sx={styles.rightColumn}>
              {orderDetails && (
                <PatientCard
                  customer={orderDetails.customer as Orders.OrderCustomerDetails}
                  createdAt={orderDetails.createdAt}
                />
              )}

              <ListCard title="Vergangene Befundungen" count={previousOrders.length}>
                {previousOrders.length === 0 ? (
                  <Typography sx={styles.emptyText}>Keine Befundungen vorhanden</Typography>
                ) : (
                  previousOrders.map((order) => (
                    <Box key={order.requestId} sx={styles.historyItem}>
                      <Typography sx={styles.historyItemDate}>{formatDate(order.createdAt)}</Typography>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography sx={styles.historyItemTitle}>{order.product.productName}</Typography>
                        <Box sx={styles.checkBadge}>✓</Box>
                      </Box>
                    </Box>
                  ))
                )}
              </ListCard>

              <ListCard title="Bestellhistorie" count={orderHistoryWithoutCurrent.length}>
                {orderHistoryWithoutCurrent.length === 0 ? (
                  <Typography sx={styles.emptyText}>Keine Bestellungen vorhanden</Typography>
                ) : (
                  orderHistoryWithoutCurrent.map((order) => (
                    <Box key={order.requestId} sx={styles.orderItem}>
                      <Box sx={styles.orderItemLeft}>
                        <Typography sx={styles.orderItemTitle}>{order.product.productName}</Typography>
                        <Typography sx={styles.orderItemDate}>{formatDate(order.createdAt)}</Typography>
                      </Box>
                      <Box
                        sx={
                          order.orderState === Orders.OrderRequestState.ALLOWED
                            ? styles.checkBadge
                            : order.orderState === Orders.OrderRequestState.DENIED
                              ? styles.deniedBadge
                              : styles.countBadge
                        }
                      >
                        {order.orderState === Orders.OrderRequestState.ALLOWED
                          ? '✓'
                          : order.orderState === Orders.OrderRequestState.DENIED
                            ? '✗'
                            : '·'}
                      </Box>
                    </Box>
                  ))
                )}
              </ListCard>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
